import { Editor } from 'components/Editor/Editor'
import { EditorRHF } from 'components/Editor/EditorRHF'
import { INote } from 'models/contact_companies'
import { useState, useMemo } from 'react'
import { Control, FieldValues, Path, useController } from 'react-hook-form'
import { CgAttachment } from 'react-icons/cg'
import { toast } from 'react-toastify'
import { API_HOST } from 'utils/http-client'
import { useFileAttachments } from 'hooks/useFileAttachments'
import {
  ALLOWED_NOTE_ATTACHMENT_TYPES,
  MAX_NOTE_ATTACHMENT_SIZE,
  getAcceptString,
} from 'constants/fileTypes'
import {
  AttachmentList,
  Attachment,
  isNewAttachment,
} from 'components/Attachments/AttachmentList'

interface NoteManagerProps<T extends FieldValues> {
  control: Control<T>
  noteName: Path<T>
  attachmentsName: Path<T>
  label?: string
  placeholder?: string
  existingNote?: INote
  viewMode?: boolean
  editorClassName?: string
  enableMentions?: boolean
  disabled?: boolean
  onAttachmentsToDelete?: (attachmentIds: number[]) => void
}

export const NoteManager = <T extends FieldValues>(
  props: NoteManagerProps<T>
) => {
  const {
    control,
    noteName,
    attachmentsName,
    label,
    placeholder,
    existingNote,
    viewMode,
    editorClassName = 'h-28',
    enableMentions = false,
    disabled = false,
  } = {
    placeholder: 'Start writing note here',
    label: 'New Note',
    ...props,
  }

  const [existingAttachments, setExistingAttachments] = useState(
    existingNote?.attachments
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([])

  const {
    field: { onChange: attachmentsOnChange, value: attachmentsValue },
  } = useController({ name: attachmentsName, control }) as {
    field: { onChange: (value: File[]) => void; value: File[] }
  }

  const {
    fileInputRef: inputRef,
    openFileSelect,
    handleFileSelect,
  } = useFileAttachments({
    allowedTypes: ALLOWED_NOTE_ATTACHMENT_TYPES,
    maxFileSize: MAX_NOTE_ATTACHMENT_SIZE,
    maxTotalSize: MAX_NOTE_ATTACHMENT_SIZE,
    onAdd: (newAttachments) => {
      const files = newAttachments
        .filter(isNewAttachment)
        .map((att) => att.file)
      attachmentsOnChange([...attachmentsValue, ...files])
    },
  })

  const handleRemoveExistingAttachment = (index: number) => {
    if (!existingAttachments) return
    const attachment = existingAttachments[index]

    // Track for deletion (API call happens on save)
    if (attachment.id) {
      setAttachmentsToDelete((prev) => {
        const updated = [...prev, attachment.id]
        props.onAttachmentsToDelete?.(updated)
        return updated
      })
    }

    // Remove from display immediately
    setExistingAttachments((existingAttachments) =>
      existingAttachments?.filter((_, i) => i !== index)
    )
    toast.info('Attachment will be removed when you save')
  }

  // Combine existing and new attachments for AttachmentList
  const allAttachments = useMemo(() => {
    const combined: Attachment[] = []

    // Add existing attachments from server
    if (existingAttachments) {
      combined.push(
        ...existingAttachments.map((att) => ({
          id: att.id,
          filename: att.original_filename,
          size: att.size || 0, // Use size from backend
          file_url: att.file?.startsWith('/')
            ? `${API_HOST}${att.file}`
            : att.file,
        }))
      )
    }

    // Add new attachments (Files)
    if (!viewMode && attachmentsValue) {
      combined.push(
        ...attachmentsValue.map((file) => ({
          file,
          filename: file.name,
          size: file.size,
        }))
      )
    }

    return combined
  }, [existingAttachments, attachmentsValue, viewMode])

  // Show summary if we have any attachments in edit mode (sizes available from backend)
  const shouldShowSummary = !viewMode && allAttachments.length > 0

  const handleRemoveAttachment = (index: number) => {
    const existingCount = existingAttachments?.length || 0

    if (index < existingCount) {
      // Remove existing attachment
      handleRemoveExistingAttachment(index)
    } else {
      // Remove new attachment
      const newIndex = index - existingCount
      const newAttachments = attachmentsValue.filter((_, i) => i !== newIndex)
      attachmentsOnChange(newAttachments)
      toast.info('Attachment removed')
    }
  }

  return (
    <div>
      {viewMode ? (
        <Editor
          value={existingNote?.content || ''}
          readOnly
          disableToolbar
          borderless
        />
      ) : (
        <EditorRHF
          name={noteName}
          className={editorClassName}
          control={control}
          label={label}
          placeholder={placeholder}
          enableMentions={enableMentions}
          disabled={disabled}
          additionalToolbarButtons={[
            <CgAttachment
              key="attachments"
              size={24}
              className={`p-1 w-8 ${
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer hover:text-[#1E734C]'
              }`}
              onClick={disabled ? undefined : openFileSelect}
            />,
          ]}
        />
      )}

      {allAttachments.length > 0 && (
        <div className="mt-3">
          <AttachmentList
            attachments={allAttachments}
            onRemove={viewMode ? undefined : handleRemoveAttachment}
            disabled={disabled}
            maxSize={MAX_NOTE_ATTACHMENT_SIZE}
            showSummary={shouldShowSummary}
          />
        </div>
      )}

      {!viewMode && (
        <input
          type="file"
          hidden
          multiple
          ref={inputRef}
          onChange={(e) => {
            // Convert existing File[] to Attachment[] for validation
            const currentAttachments = attachmentsValue.map((file) => ({
              file,
              filename: file.name,
              size: file.size,
            }))
            handleFileSelect(e, currentAttachments)
          }}
          accept={getAcceptString(ALLOWED_NOTE_ATTACHMENT_TYPES)}
          disabled={disabled}
        />
      )}
    </div>
  )
}
